import {
  Autocomplete,
  AutocompleteProps
} from '@dtx-company/design-system/src/components/Autocomplete/Autocomplete'
import { Box } from '@dtx-company/design-system/src'
import { CountryCode } from '@dtx-company/shared-components/src'
import { Path, PathValue, useFormContext, useWatch } from 'react-hook-form'
import { ScanDestinationFormTypes } from './ScanDestination.types'
import { TextField } from '@mui/material'
import { useEffect } from 'react'
import COUNTRY_CODES, {
  USA_COUNTRY_CODE
} from '@dtx-company/shared-components/src/constants/country-codes'
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined'

type CountryCodeOption = { label: string; value: CountryCode | string }
type CountryCodeAutocompleteProps = Pick<
  AutocompleteProps<CountryCodeOption, false, true, false>,
  'onChange' | 'value' | 'readOnly'
> & { width?: string }

export const CountryCodeAutocomplete = ({
  onChange,
  value,
  readOnly,
  width
}: CountryCodeAutocompleteProps): JSX.Element => (
  <Box
    sx={theme => ({
      width: width || theme.spacing(35),
      '& .MuiAutocomplete-root .MuiFilledInput-root, & .MuiAutocomplete-root  .MuiFilledInput-root:focus-within':
        {
          p: 1.55
        }
    })}
  >
    <Autocomplete<CountryCodeOption, false, true, false>
      disableClearable
      label="Country Code"
      defaultValue={USA_COUNTRY_CODE}
      value={value}
      renderInput={params => (
        <TextField
          {...params}
          InputProps={{
            ...params.InputProps,
            sx: { height: '3rem' },
            startAdornment: <LocalPhoneOutlinedIcon />,
            inputProps: {
              ...params.inputProps,
              'aria-label': 'Country Code'
            }
          }}
        />
      )}
      options={COUNTRY_CODES}
      isOptionEqualToValue={(option, value) => {
        return (
          (typeof option.value === 'string'
            ? option.value
            : (option.value as CountryCode).phone) ===
          (typeof value.value === 'string' ? value.value : (value.value as CountryCode).phone)
        )
      }}
      onChange={onChange}
      readOnly={readOnly}
      disabled={readOnly}
    />
  </Box>
)

type ScanDestinationPhoneInputProps<T extends ScanDestinationFormTypes> = {
  countryCodeName: Path<T>
  readOnly?: boolean
}

function getCountryCodeOption(countryCode: any): CountryCodeOption {
  const code = countryCode ?? '1'
  return { value: code.toString(), label: `+${code}` }
}

export const ScanDestinationPhoneInput = <T extends ScanDestinationFormTypes>({
  countryCodeName,
  readOnly
}: ScanDestinationPhoneInputProps<T>): JSX.Element => {
  const { register, setValue } = useFormContext<T>()
  const countryCode = useWatch<T>({ name: countryCodeName })
  const countryCodeValue = getCountryCodeOption(countryCode)

  useEffect(() => {
    register(countryCodeName)
  }, [register, countryCodeName])

  return (
    <CountryCodeAutocomplete
      value={countryCodeValue}
      onChange={(_, option) => {
        setValue(countryCodeName, (option?.value as CountryCode)?.phone as PathValue<T, Path<T>>, {
          shouldDirty: true
        })
      }}
      readOnly={readOnly}
    />
  )
}
